/**
 * The register's printer (T07-25): which driver this tablet uses (settings, default from the
 * register's `receiptPrinter` config in the admin), the print queue, and the helpers the screens
 * call — the receipt / Z-report / 買取明細 bytes always come from pos-service.
 */
import type { PosDatabase } from "../db";
import { readSetting, writeSetting } from "../db";
import { BridgePrinter } from "./bridge";
import { MockPrinter } from "./mock";
import { PrintQueue } from "./queue";
import { PrinterError, type PrinterConnection, type PrinterDriver } from "./types";
import { WebBluetoothPrinter } from "./webbluetooth";
import { WebUsbPrinter, type UsbIdentity } from "./webusb";

export * from "./types";
export { BridgePrinter } from "./bridge";
export { MockPrinter } from "./mock";
export { PrintQueue, type NewPrintJob } from "./queue";
export { WebBluetoothPrinter } from "./webbluetooth";
export { WebUsbPrinter, sameUsbDevice, usbIdentityOf, type UsbIdentity } from "./webusb";
export {
  LabelPrinter,
  DEFAULT_LABEL_SETTINGS,
  LABEL_SETTINGS_KEY,
  type LabelPrinterSettings,
} from "./labels";
export { DRAWER_KICK, base64ToBytes, bytesToBase64 } from "./escpos";

export interface PrinterSettings {
  connection: PrinterConnection;
  paperWidth: 58 | 80;
  bridgeUrl: string | null;
  bridgeToken: string | null;
  bridgePrinter: string | null;
  /** Open the drawer after a cash sale (the register has a drawer on its printer). */
  drawer: boolean;
  /** The USB printer picked in 設定 (a label printer may be plugged in too — T07-35). */
  usbDevice?: UsbIdentity | null;
}

export const PRINTER_SETTINGS_KEY = "printer";

export const DEFAULT_PRINTER_SETTINGS: PrinterSettings = {
  connection: "none",
  paperWidth: 80,
  bridgeUrl: null,
  bridgeToken: null,
  bridgePrinter: null,
  drawer: true,
};

/** The admin's register config (`receiptPrinter`) → this tablet's defaults. */
export function settingsFromRegister(
  config: Record<string, unknown> | null | undefined,
  cashDrawer: boolean,
): PrinterSettings {
  // `kind` / `paper_width_mm`: registers seeded before T07-37 stored the raw fixture shape
  const raw = config?.connection ?? config?.kind;
  const connection = typeof raw === "string" ? raw : "none";
  const mapped: PrinterConnection =
    connection === "sunmi"
      ? "webusb"
      : ((["webusb", "bluetooth", "network", "mock"] as const).find((c) => c === connection) ??
        "none");
  const paper =
    config?.paperMm === 58 || config?.paper_mm === 58 || config?.paper_width_mm === 58 ? 58 : 80;
  const address = typeof config?.address === "string" ? config.address : null;
  return {
    ...DEFAULT_PRINTER_SETTINGS,
    connection: mapped,
    paperWidth: paper,
    bridgeUrl:
      mapped === "network" && address
        ? address.startsWith("http")
          ? address
          : `https://${address}`
        : null,
    drawer: cashDrawer,
  };
}

export class PrinterManager {
  private settings: PrinterSettings = DEFAULT_PRINTER_SETTINGS;
  private current: PrinterDriver | null = null;
  readonly queue: PrintQueue;
  /** Tests / dev: the mock printer used when `connection` is `mock` or `none` in dev. */
  readonly mock = new MockPrinter();
  private readonly db: PosDatabase;
  private readonly listeners = new Set<() => void>();

  constructor(db: PosDatabase, queue?: (driver: () => PrinterDriver) => PrintQueue) {
    this.db = db;
    this.queue = queue
      ? queue(() => this.driver())
      : new PrintQueue({ db, driver: () => this.driver() });
  }

  async load(fallback?: PrinterSettings): Promise<PrinterSettings> {
    this.settings = await readSetting(
      this.db,
      PRINTER_SETTINGS_KEY,
      fallback ?? DEFAULT_PRINTER_SETTINGS,
    );
    this.current = null;
    this.emit();
    return this.settings;
  }

  async save(settings: PrinterSettings): Promise<void> {
    this.settings = settings;
    this.current = null;
    await writeSetting(this.db, PRINTER_SETTINGS_KEY, settings);
    this.emit();
  }

  /** Remember the USB printer the staff picked, without dropping the open connection. */
  async rememberUsb(identity: UsbIdentity): Promise<void> {
    this.settings = { ...this.settings, usbDevice: identity };
    await writeSetting(this.db, PRINTER_SETTINGS_KEY, this.settings);
    this.emit();
  }

  get config(): PrinterSettings {
    return this.settings;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const listener of [...this.listeners]) {
      listener();
    }
  }

  driver(): PrinterDriver {
    if (this.current) {
      return this.current;
    }
    const s = this.settings;
    this.current =
      s.connection === "webusb"
        ? new WebUsbPrinter(undefined, {
            device: s.usbDevice ?? null,
            onChosen: (identity) => void this.rememberUsb(identity),
          })
        : s.connection === "bluetooth"
          ? new WebBluetoothPrinter()
          : s.connection === "network"
            ? new BridgePrinter({
                url: s.bridgeUrl ?? "",
                token: s.bridgeToken,
                printer: s.bridgePrinter,
              })
            : s.connection === "mock"
              ? this.mock
              : new NoPrinter();
    return this.current;
  }
}

/** `connection: none` — nothing configured yet: every print fails with a clear reason. */
class NoPrinter implements PrinterDriver {
  readonly kind = "none" as const;
  supported(): boolean {
    return true;
  }
  async connect() {
    return { connected: false, error: "no_printer" as const };
  }
  async print(): Promise<void> {
    throw new PrinterError("no_printer");
  }
  async openDrawer(): Promise<void> {
    throw new PrinterError("no_printer");
  }
  async status() {
    return { connected: false, error: "no_printer" as const };
  }
  async disconnect(): Promise<void> {
    // nothing
  }
}
